import { FacetResultDwr, FacetValue, useLSSearch } from "./LSSearch";
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from "@mui/material";
import { axios } from "@tlc/ls2pac/components/AxiosInstanceManager";
import { FacetFilter } from "../../../../dto/FacetFilter";
import React from "react";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { FacetDisplayInformation } from "@tlc/ls2pac/dto/FacetDisplayInformation";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useErrorDialog } from "@tlc/ui/dialog/error/ErrorDialogContext";

interface FacetProps {
  facet: FacetDisplayInformation;
}
export const FacetControl = ({ facet }: FacetProps) => {
  const {
    searchParams: { facetFilters },
    clearFacetFilter,
    addFacetFilter,
    loadingFacets,
    facetResults,
  } = useLSSearch();
  const { translate } = useLanguage();
  const [open, setOpen] = useState<boolean>(false);
  const [moreResults, setMoreResults] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>(true);
  const [facetValues, setFacetValues] = useState<FacetValue[] | null>(null);
  const [hidden, setHidden] = useState<boolean>(false);
  const { handleRequestError } = useErrorDialog();

  const activeFilters = useMemo(() => {
    return facetFilters.filter((f) => f.facetName === facet.name);
  }, [facetFilters]);

  const secondaryText = useMemo(() => {
    if (activeFilters?.length > 0) {
      return activeFilters.map((af) => af.facetDisplay).join(", ");
    } else if (loadingFacets) {
      return <Skeleton width={40} />;
    } else if (hidden) {
      return translate("noFacetsRemaining");
    } else {
      return (
        <span style={{ textTransform: "capitalize" }}>{translate("all")}</span>
      );
    }
  }, [activeFilters, loadingFacets, hidden]);

  useEffect(() => {
    if (!loadingFacets && facetResults) {
      const match = facetResults.find((fr) => fr.facetName === facet.name);
      if (match) {
        setFacetValues(match.facetValues);
        setMoreResults(match.moreResults);
        setLoading(false);
        setHidden(false);
      } else if (activeFilters?.length > 0) {
        setLoading(false);
        setHidden(false);
      } else {
        setHidden(true);
        setLoading(false);
      }
    } else {
      if (facetFilters.length === 0) {
        setHidden(false);
      }
      setLoading(true);
      setOpen(false);
      setFacetValues(null);
      setMoreResults(false);
    }
  }, [facetResults, loadingFacets, activeFilters, facetFilters]);

  // TODO work on fetch more expand animation
  const fetchMore = async () => {
    try {
      setLoading(true);
      const count = 6; // This matches the old frontend code but is ignored by the API
      const start = facetValues?.length || 0;
      const { data } = await axios.get<FacetResultDwr>(
        `/facets/getMoreFacetResults/${facet.name}/${start}/${count}`,
      );
      setFacetValues((prev) => [...(prev || []), ...data.facetValues]);
      setMoreResults(data.moreResults);
    } catch (e: unknown) {
      handleRequestError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    if (!open) {
      if (!facetValues || facetValues.length === 0) {
        await fetchMore();
        setOpen(true);
      } else {
        setOpen(true);
      }
    } else {
      setOpen(false);
    }
  };

  const handleClickMore = async () => {
    await fetchMore();
  };

  const handleFacetValueClick = ({ value, displayValue }: FacetValue) => {
    addFacetFilter({
      facetName: facet.name,
      facetValue: value,
      facetDisplay: displayValue,
    });
  };

  const handleFacetValueClickRemove = (facetFilter: FacetFilter) => {
    clearFacetFilter(facetFilter);
  };

  return (
    <>
      <Collapse in={!hidden} timeout="auto" unmountOnExit component="li">
        <ListItemButton
          divider={!open}
          sx={{ gap: 2 }}
          onClick={handleClick}
          aria-expanded={open}
          aria-controls={`facet-panel-${facet.name}`}
          id={`facet-header-${facet.name}`}
        >
          <ListItemText
            sx={{ my: 0 }}
            primary={translate(facet.displayName)}
            secondaryTypographyProps={{ color: "secondary" }}
            secondary={secondaryText}
          />
          {open ? (
            <ExpandLess />
          ) : loading && !facetValues ? (
            <CircularProgress size="24px" color="inherit" />
          ) : (
            <ExpandMore />
          )}
        </ListItemButton>
      </Collapse>
      <Collapse in={open} timeout="auto" unmountOnExit component="li">
        <List
          id={`facet-panel-${facet.name}`}
          aria-label={`${facet.displayName}`}
          aria-busy={loading}
          disablePadding
          dense={true}
        >
          {activeFilters &&
            activeFilters.map((af) => (
              <ListItem key={af.facetValue} disablePadding>
                <ListItemButton
                  role="checkbox"
                  aria-checked={true}
                  aria-labelledby={`facet-label-active-${af.id}`}
                  onClick={() => handleFacetValueClickRemove(af)}
                >
                  <ListItemIcon>
                    <CheckBoxIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    inset={false}
                    id={`facet-label-active-${af.id}`}
                    primary={af.facetDisplay}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          {facetValues &&
            facetValues.map((fv) => (
              <ListItem key={fv.value} disablePadding>
                <ListItemButton
                  onClick={() => handleFacetValueClick(fv)}
                  role="checkbox"
                  aria-checked={false}
                  aria-label={`${fv.displayValue}`}
                  aria-description={`${String(fv.count)} result${
                    fv.count > 1 ? "s" : ""
                  }`}
                >
                  <ListItemIcon>
                    <CheckBoxOutlineBlankIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    id={`facet-label-${fv.value}`}
                    inset={false}
                    primary={fv.displayValue}
                  />
                  <Chip id={`facet-count-${fv.value}`} label={fv.count} />
                </ListItemButton>
              </ListItem>
            ))}
          {moreResults && (
            <ListItem>
              <Button
                fullWidth
                loading={loading}
                variant="outlined"
                loadingPosition="start"
                startIcon={<AddIcon />}
                onClick={handleClickMore}
              >
                {translate("showMore")}
              </Button>
            </ListItem>
          )}
        </List>
        <Divider />
      </Collapse>
    </>
  );
};
